/**
 * ProfileTab Component
 *
 * Contact profile with inline editing (Jira-style).
 * Displays relationship info, basic info, social links, notes, tags, and aliases.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi, CONTACT_RELATION_TYPES } from '@/services/contactsApi';
import type { Contact } from '../types';
import EditableField from '@/components/tasks/EditableField';
import { EmailsSection, PhonesSection, CompaniesSection } from '../sections';

interface ProfileTabProps {
  contact: Contact;
  onUpdate: () => void;
  onError: (message: string) => void;
  onNavigateToCompany?: (companyId: string) => void;
}

export default function ProfileTab({
  contact,
  onUpdate,
  onError,
  onNavigateToCompany,
}: ProfileTabProps) {
  const { t } = useTranslation('people');
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [showAddAlias, setShowAddAlias] = useState(false);
  const [newAlias, setNewAlias] = useState('');

  const handleFieldUpdate = async (field: string, value: string | null) => {
    try {
      await contactsApi.update(contact.id, { [field]: value });
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to update field');
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;
    const newTags = [...(contact.tags || []), newTag.trim()];
    try {
      await contactsApi.update(contact.id, { tags: newTags });
      onUpdate();
      setNewTag('');
      setShowAddTag(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add tag');
    }
  };

  const handleRemoveTag = async (index: number) => {
    const newTags = contact.tags.filter((_, idx) => idx !== index);
    try {
      await contactsApi.update(contact.id, { tags: newTags });
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove tag');
    }
  };

  const handleAddAlias = async () => {
    if (!newAlias.trim()) return;
    const newAliases = [...(contact.aliases || []), newAlias.trim()];
    try {
      await contactsApi.update(contact.id, { aliases: newAliases });
      onUpdate();
      setNewAlias('');
      setShowAddAlias(false);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add alias');
    }
  };

  const handleRemoveAlias = async (index: number) => {
    const newAliases = contact.aliases.filter((_, idx) => idx !== index);
    try {
      await contactsApi.update(contact.id, { aliases: newAliases });
      onUpdate();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to remove alias');
    }
  };

  return (
    <div className="space-y-6">
      {/* Basic Info Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.basic_info')}</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.first_name')}</label>
            <EditableField
              type="text"
              value={contact.first_name}
              onSave={(v) => handleFieldUpdate('first_name', v)}
              placeholder={t('profile_tab.placeholders.first_name')}
              emptyText={t('profile_tab.empty_text.first_name')}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.last_name')}</label>
            <EditableField
              type="text"
              value={contact.last_name || ''}
              onSave={(v) => handleFieldUpdate('last_name', v || null)}
              placeholder={t('profile_tab.placeholders.last_name')}
              emptyText={t('profile_tab.empty_text.last_name')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.title')}</label>
            <EditableField
              type="text"
              value={contact.title || ''}
              onSave={(v) => handleFieldUpdate('title', v || null)}
              placeholder={t('profile_tab.placeholders.title')}
              emptyText={t('profile_tab.empty_text.title')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.department')}</label>
            <EditableField
              type="text"
              value={contact.department || ''}
              onSave={(v) => handleFieldUpdate('department', v || null)}
              placeholder={t('profile_tab.placeholders.department')}
              emptyText={t('profile_tab.empty_text.department')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.location')}</label>
            <EditableField
              type="text"
              value={contact.location || ''}
              onSave={(v) => handleFieldUpdate('location', v || null)}
              placeholder={t('profile_tab.placeholders.location')}
              emptyText={t('profile_tab.empty_text.location')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.timezone')}</label>
            <EditableField
              type="text"
              value={contact.timezone || ''}
              onSave={(v) => handleFieldUpdate('timezone', v || null)}
              placeholder={t('profile_tab.placeholders.timezone')}
              emptyText={t('profile_tab.empty_text.timezone')}
            />
          </div>
        </div>
      </div>

      {/* Relationship Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.relationship')}</h3>
        <div className="space-y-4">
          {/* Short Bio */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.bio')}</label>
            <div className="relative">
              <EditableField
                type="textarea"
                value={contact.short_bio || ''}
                onSave={(v) => handleFieldUpdate('short_bio', v || null)}
                placeholder={t('profile_tab.placeholders.bio')}
                emptyText={t('profile_tab.empty_text.bio')}
                rows={2}
              />
              <div className="text-xs text-gray-400 text-right mt-1">
                {(contact.short_bio || '').length}/200
              </div>
            </div>
          </div>
          {/* Relationship Goal */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.our_goal')}</label>
            <div className="relative">
              <EditableField
                type="textarea"
                value={contact.relationship_goal || ''}
                onSave={(v) => handleFieldUpdate('relationship_goal', v || null)}
                placeholder={t('profile_tab.placeholders.our_goal')}
                emptyText={t('profile_tab.empty_text.our_goal')}
                rows={2}
              />
              <div className="text-xs text-gray-400 text-right mt-1">
                {(contact.relationship_goal || '').length}/200
              </div>
            </div>
          </div>
          {/* Relation Type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.relation_type')}</label>
            <EditableField
              type="select"
              value={contact.relationship_to_user || 'unknown'}
              onSave={(v) => handleFieldUpdate('relationship_to_user', v)}
              options={CONTACT_RELATION_TYPES.map(r => ({ value: r.value, label: r.label }))}
            />
          </div>
        </div>
      </div>

      {/* Contact Info Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.contact')}</h3>
        <div className="space-y-6">
          <EmailsSection
            emails={contact.emails || []}
            entityId={contact.id}
            entityType="contact"
            onUpdate={onUpdate}
            onError={onError}
          />
          <PhonesSection
            phones={contact.phones || []}
            entityId={contact.id}
            entityType="contact"
            onUpdate={onUpdate}
            onError={onError}
          />
        </div>
      </div>

      {/* Companies Section */}
      <CompaniesSection
        companies={contact.companies || []}
        contactId={contact.id}
        onUpdate={onUpdate}
        onError={onError}
        onCompanyClick={onNavigateToCompany}
      />

      {/* Social Links Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.social')}</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.linkedin_url')}</label>
            <EditableField
              type="text"
              value={contact.linkedin_url || ''}
              onSave={(v) => handleFieldUpdate('linkedin_url', v || null)}
              placeholder={t('profile_tab.placeholders.linkedin')}
              emptyText={t('profile_tab.empty_text.linkedin')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.twitter_handle')}</label>
            <EditableField
              type="text"
              value={contact.twitter_handle || ''}
              onSave={(v) => handleFieldUpdate('twitter_handle', v || null)}
              placeholder={t('profile_tab.placeholders.twitter')}
              emptyText={t('profile_tab.empty_text.twitter')}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1">{t('profile_tab.fields.github_username')}</label>
            <EditableField
              type="text"
              value={contact.github_username || ''}
              onSave={(v) => handleFieldUpdate('github_username', v || null)}
              placeholder={t('profile_tab.placeholders.github')}
              emptyText={t('profile_tab.empty_text.github')}
            />
          </div>
        </div>
        {/* Social Links Icons */}
        {(contact.linkedin_url || contact.twitter_handle || contact.github_username) && (
          <div className="mt-4 flex gap-3 border-t border-gray-100 pt-4">
            {contact.linkedin_url && (
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title={t('profile_tab.tooltips.linkedin')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
            )}
            {contact.twitter_handle && (
              <a
                href={`https://twitter.com/${contact.twitter_handle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-50 rounded transition-colors"
                title={t('profile_tab.tooltips.twitter')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                </svg>
              </a>
            )}
            {contact.github_username && (
              <a
                href={`https://github.com/${contact.github_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                title={t('profile_tab.tooltips.github')}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Notes Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.notes')}</h3>
        <EditableField
          type="textarea"
          value={contact.notes || ''}
          onSave={(v) => handleFieldUpdate('notes', v || null)}
          placeholder={t('profile_tab.placeholders.our_goal')}
          emptyText={t('profile_tab.empty_text.notes')}
          rows={4}
        />
      </div>

      {/* Tags Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.tags')}</h3>
        <div className="flex flex-wrap gap-2 items-center">
          {contact.tags && contact.tags.length > 0 ? (
            contact.tags.map((tag, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded group">
                <button
                  onClick={() => handleRemoveTag(i)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title={t('profile_tab.tooltips.remove_tag')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {tag}
              </span>
            ))
          ) : (
            <span className="text-gray-400 text-sm">{t('profile_tab.no_tags')}</span>
          )}
          {showAddTag ? (
            <div className="inline-flex items-center gap-1">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newTag.trim()) {
                    e.preventDefault();
                    handleAddTag();
                  }
                  if (e.key === 'Escape') {
                    setNewTag('');
                    setShowAddTag(false);
                  }
                }}
                placeholder={t('profile_tab.new_tag_placeholder')}
                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleAddTag}
                disabled={!newTag.trim()}
                className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {t('profile_tab.add')}
              </button>
              <button
                onClick={() => { setNewTag(''); setShowAddTag(false); }}
                className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
              >
                {t('profile_tab.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddTag(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('profile_tab.add_tag')}
            </button>
          )}
        </div>
      </div>

      {/* Aliases Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-medium text-gray-500 uppercase mb-4">{t('profile_tab.sections.aliases')}</h3>
        <p className="text-xs text-gray-400 mb-3">{t('profile_tab.aliases_description')}</p>
        <div className="flex flex-wrap gap-2 items-center">
          {contact.aliases && contact.aliases.length > 0 ? (
            contact.aliases.map((alias, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded group">
                <button
                  onClick={() => handleRemoveAlias(i)}
                  className="text-purple-400 hover:text-red-500 transition-colors"
                  title={t('profile_tab.tooltips.remove_alias')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                {alias}
              </span>
            ))
          ) : (
            <span className="text-gray-400 text-sm">{t('profile_tab.no_aliases')}</span>
          )}
          {showAddAlias ? (
            <div className="inline-flex items-center gap-1">
              <input
                type="text"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newAlias.trim()) {
                    e.preventDefault();
                    handleAddAlias();
                  }
                  if (e.key === 'Escape') {
                    setNewAlias('');
                    setShowAddAlias(false);
                  }
                }}
                placeholder={t('profile_tab.new_alias_placeholder')}
                className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={handleAddAlias}
                disabled={!newAlias.trim()}
                className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {t('profile_tab.add')}
              </button>
              <button
                onClick={() => { setNewAlias(''); setShowAddAlias(false); }}
                className="px-2 py-1 text-sm text-gray-600 hover:bg-gray-200 rounded"
              >
                {t('profile_tab.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddAlias(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('profile_tab.add_alias')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
